import {
  ConditionValueSource,
  HeliosRecordSystemDatasource,
  LogicalOperator,
  TemplatePresentationItemDisplay,
  type HeliosRecordDataTablesColumn,
  type HeliosRecordDataTablesReq,
} from 'types/dataHelios';
import type { FilterCondition } from 'types/tableFilters';

const tryExtractBrowseId = (value: string): string => {
  try {
    return JSON.parse(value)?.id || value;
  } catch {
    return value;
  }
};

const toHeliosApiFilters = (
  activeFilters: FilterCondition[],
  columns: HeliosRecordDataTablesColumn[] | undefined
): HeliosRecordDataTablesReq['filters'] =>
  activeFilters
    .map((filter) => {
      const column = columns?.find((col) => col.columnKey === filter.columnKey);
      if (!column) return null;

      let fromVal = filter.fromValue;
      let toVal = filter.toValue;

      if (fromVal && toVal) {
        const fromNum = Number(fromVal);
        const toNum = Number(toVal);

        if (!isNaN(fromNum) && !isNaN(toNum) && fromNum > toNum) {
          fromVal = toVal;
          toVal = filter.fromValue;
        } else if (fromVal > toVal && column.dataType === 'DateTime') {
          fromVal = toVal;
          toVal = filter.fromValue;
        }
      }

      return {
        leftBracket: filter.leftBracket || false,
        rightBracket: filter.rightBracket || false,
        isBlank: filter.isBlank ?? null,
        datasource: {
          templateDataItemId: column.itemId,
          aggregationGroupId: column.aggregationGroupId ?? undefined,
          systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
        },
        leftLogicalOperator: filter.logicalOperator || LogicalOperator.And,
        comparisonOperator: filter.operator,
        value: {
          conditionValueSource: ConditionValueSource.None,
          staticValue:
            column.displayType === TemplatePresentationItemDisplay.Browse
              ? filter.value
                ? tryExtractBrowseId(filter.value)
                : filter.value
              : filter.value,
          staticValues: filter.staticValues && filter.staticValues.length > 0 ? filter.staticValues : undefined,
          fromValue: fromVal,
          toValue: toVal,
          systemDatasource: HeliosRecordSystemDatasource.HeliosUser,
          templateDataItemId: column.itemId,
        },
      };
    })
    .filter((filter): filter is NonNullable<typeof filter> => filter !== null);

// User conditions are bracketed so an OR inside them cannot escape the AND-joined base scope.
const appendHeliosFilterGroup = (
  baseFilters: HeliosRecordDataTablesReq['filters'],
  userFilters: HeliosRecordDataTablesReq['filters']
): HeliosRecordDataTablesReq['filters'] => {
  if (userFilters.length === 0) return baseFilters;
  if (baseFilters.length === 0) return userFilters;

  const lastIndex = userFilters.length - 1;
  const groupedUserFilters = userFilters.map((filter, index) => ({
    ...filter,
    leftBracket: index === 0 ? true : filter.leftBracket,
    rightBracket: index === lastIndex ? true : filter.rightBracket,
    leftLogicalOperator: index === 0 ? LogicalOperator.And : filter.leftLogicalOperator,
  }));

  return [...baseFilters, ...groupedUserFilters];
};

export { toHeliosApiFilters, appendHeliosFilterGroup };
